import React, { useEffect, useState } from "react";
import Table from "@components/table";
import { Product } from "@apis/index";
import Column from "@components/column";
import Row from "@components/row";
import Typography from "@components/typography";
import Header from "@commons/header";
import { toast } from "react-toastify";
import Button from "@components/button";
import theme from "@utils/theme";
import { useSearchParams } from "react-router-dom";
import {
  Loader2,
  AlertCircle,
  CheckCircle2,
  Clock,
  PlayCircle,
} from "lucide-react";

const SmoothProgressBar = ({ targetProgress, status }: { targetProgress: number, status: string }) => {
  const [displayProgress, setDisplayProgress] = useState(targetProgress);

  useEffect(() => {
    if (status !== "processing") {
      setDisplayProgress(targetProgress);
      return;
    }

    if (targetProgress > displayProgress) {
      const diff = targetProgress - displayProgress;
      const step = diff / 10;
      const catchUpInterval = setInterval(() => {
        setDisplayProgress(prev => {
          if (prev >= targetProgress) {
            clearInterval(catchUpInterval);
            return targetProgress;
          }
          return prev + step;
        });
      }, 50);
      return () => clearInterval(catchUpInterval);
    }

    const crawlInterval = setInterval(() => {
      setDisplayProgress(prev => {
        if (prev >= 98 || prev >= targetProgress + 15) return prev;
        return prev + 0.1;
      });
    }, 100);

    return () => clearInterval(crawlInterval);
  }, [targetProgress, status]);

  return (
    <Column stylecolumn="gap: 8px">
      <Row styleRow="justify-content: space-between; align-items: center">
        <Typography size={11} weight={600} styleText="color: #666">
          {Math.floor(displayProgress)}%
        </Typography>
        {status === "processing" && (
          <Typography size={10} styleText="color: #3b82f6">
            Processing Media...
          </Typography>
        )}
      </Row>
      <div style={{ 
        width: "100%", 
        background: "#f1f5f9", 
        borderRadius: "10px", 
        height: "12px", 
        overflow: "hidden", 
        position: "relative",
        boxShadow: "inset 0 2px 4px rgba(0,0,0,0.05)"
      }}>
         <div style={{ 
           width: `${displayProgress}%`, 
           height: "100%", 
           background: status === "failed" ? "#ef4444" : "linear-gradient(90deg, #3b82f6, #60a5fa, #3b82f6)",
           backgroundSize: "200% 100%",
           transition: "width 0.2s linear",
           boxShadow: "0 0 10px rgba(59, 130, 246, 0.4)",
           position: "relative",
           animation: status === "processing" ? "shimmer 2s infinite linear" : "none"
         }}>
            <div style={{
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)",
              animation: "pulse 1.5s infinite"
            }} />
         </div>
      </div>
    </Column>
  );
};

const BackgroundTasks = () => {
  const [tasks, setTasks] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();

  const fetchTasks = async (p = page, silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res: any = await Product.getTasks({ params: { page: p, limit: 10 } });
      setTasks(res.items || []);
      setTotalItems(res.totalItems || 0);
    } catch (error) {
      console.error(error);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchTasks(page);
  }, [page]);

  useEffect(() => {
    const hasActiveTasks = tasks.some(t => t.status === "pending" || t.status === "processing");
    let interval: any;
    if (hasActiveTasks) {
      interval = setInterval(() => fetchTasks(page, true), 3000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [tasks, page]);

  const handleRetry = async (taskId: number) => {
    try {
      await Product.retryTask({ id: taskId });
      toast.success("Retrying task...");
      fetchTasks();
    } catch (error) {
      toast.error("Failed to retry task");
    }
  };

  const columns = [
    {
      title: "Product Details",
      key: "data",
      render: (task: any) => (
        <Column stylecolumn="gap: 4px">
          <Typography size={14} weight={600}>{task.data?.name || "Unnamed Product"}</Typography>
          <Typography size={12} styleText="color: #666">Store ID: {task.data?.storeId}</Typography>
        </Column>
      ),
    },
    {
      title: "Status",
      key: "status",
      render: (task: any) => {
        const config: any = {
          completed: { color: "#22c55e", bg: "#f0fdf4", icon: CheckCircle2, text: "Completed" },
          failed: { color: "#ef4444", bg: "#fef2f2", icon: AlertCircle, text: "Failed" },
          processing: { color: "#3b82f6", bg: "#eff6ff", icon: Loader2, text: "Processing" },
          pending: { color: "#f59e0b", bg: "#fffbeb", icon: Clock, text: "In Queue" },
        };
        const status = config[task.status] || config.pending;
        const Icon = status.icon;
        
        return (
          <Row styleRow={`background: ${status.bg}; color: ${status.color}; padding: 6px 12px; border-radius: 20px; gap: 6px; width: fit-content; align-items: center`}>
            <Icon size={14} className={task.status === "processing" ? "animate-spin" : ""} />
            <Typography size={12} weight={600}>{status.text}</Typography>
          </Row>
        );
      },
    },
    {
      title: "Upload Progress",
      key: "progress",
      width: "350px",
      render: (task: any) => (
        <SmoothProgressBar targetProgress={task.progress} status={task.status} />
      ),
    },
    {
      title: "Logs / Error",
      key: "error",
      render: (task: any) => (
        <Typography size={12} styleText={`color: ${task.status === "failed" ? "#ef4444" : "#64748b"}; font-style: italic`}>
          {task.status === "failed" ? task.error : task.status === "completed" ? "Successfully uploaded all assets" : "Preparing files for cloud storage..."}
        </Typography>
      ),
    },
    {
      title: "Timestamp",
      key: "createdAt",
      render: (task: any) => (
        <Typography size={12} styleText="color: #64748b">
          {new Date(task.createdAt).toLocaleString()}
        </Typography>
      ),
    },
    {
      title: "Action",
      key: "action",
      render: (task: any) => (
        <div style={{ display: "flex", gap: "10px" }}>
          {task.status === "failed" && (
            <Button 
              title="Retry" 
              icon={PlayCircle}
              onClick={() => handleRetry(task.id)} 
              styleButton="padding: 6px 14px; font-size: 12px; height: 32px" 
            />
          )}
        </div>
      ),
    },
  ];

  return (
    <Column stylecolumn="padding: 24px; gap: 24px; background: #fafafa; min-height: 100vh">
      <Header 
        title="Background Upload Monitor" 
        icon={Clock}
      />
      
      <div style={{ background: "#fff", borderRadius: "16px", padding: "20px", boxShadow: "0 4px 6px -1px rgb(0 0 0 / 0.1)" }}>
        <Table
          columns={columns}
          data={tasks}
          loading={loading}
          pagination={{
            total: totalItems,
            current: page,
            pageSize: 10,
            onChange: (p: number) => setPage(p),
          }}
        />
      </div>
    </Column>
  );
};

export default BackgroundTasks;
